// Tactmark API client talks to Cloudflare Pages Functions (/api/*)
// No secrets live in this file or anywhere in the browser bundle.
// Every authenticated request sends the session token in the Authorization header;
// the backend derives the organization (tenant) from that token never from client input.
//
// Platform Admin uses a completely separate token (tactmark_admin_token, a
// different localStorage key) and a separate request helper (adminRequest),
// so an organization/client session can never be reused against admin
// endpoints, and vice versa.

const BASE = '/api';

function getToken() {
  return localStorage.getItem('tactmark_token');
}

function getAdminToken() {
  return localStorage.getItem('tactmark_admin_token');
}

function clearSessionOnUnauthorized(path, token, storageKey, loginPath) {
  // Only clear stored sessions for authenticated API calls — never on login failures.
  if (!token) return;
  if (path.startsWith('/auth/login') || path.startsWith('/auth/admin-login') || path.startsWith('/auth/signup')) return;
  try { localStorage.removeItem(storageKey); } catch (_) {}
  if (typeof window !== 'undefined' && window.location && !window.location.pathname.startsWith(loginPath)) {
    window.location.assign(loginPath);
  }
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    if (res.status === 401) clearSessionOnUnauthorized(path, token, 'tactmark_token', '/login');
    const err = await res.json().catch(() => ({}));
    const message = err.error || `Request failed: ${res.status}`;
    const error = new Error(message);
    error.status = res.status;
    throw error;
  }
  const data = await res.json().catch(() => ({}));
  // Attach the HTTP status so callers can distinguish 202 Accepted (async work
  // still in progress) from 200 OK (completed). 202 must NOT be treated as done.
  try { Object.defineProperty(data, '__status', { value: res.status, enumerable: false }); } catch (_) {}
  return data;
}

async function adminRequest(path, options = {}) {
  const token = getAdminToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    if (res.status === 401) clearSessionOnUnauthorized(path, token, 'tactmark_admin_token', '/admin/login');
    const err = await res.json().catch(() => ({}));
    const message = err.error || `Request failed: ${res.status}`;
    const error = new Error(message);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export const auth = {
  signup: (orgName, country, email, password, organizationType, teamName, confirmPassword, plan, acceptedTerms) =>
    request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ orgName, country, email, password, confirmPassword, organizationType, teamName, plan, acceptedTerms })
    }),
  login: (email, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: () => request('/auth/me'),
  logout: () => request('/auth/logout', { method: 'POST' }).catch(() => {}),
  forgotPassword: (email) => request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, password) => request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) })
};

// Platform Admin auth entirely separate login, session, and storage from `auth` above.
export const adminAuth = {
  login: (email, password) =>
    adminRequest('/auth/admin-login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: () => adminRequest('/auth/admin-me'),
  logout: () => adminRequest('/auth/admin-logout', { method: 'POST' }).catch(() => {})
};

export const api = {
  // Teams
  getTeams: () => request('/teams'),
  createTeam: (team) => request('/teams', { method: 'POST', body: JSON.stringify(team) }),
  updateTeam: (id, name) => request('/teams', { method: 'PATCH', body: JSON.stringify({ id, name }) }),

  // Players
  getPlayers: (teamId) => request(`/players${teamId ? `?team_id=${teamId}` : ''}`),
  createPlayer: (player) => request('/players', { method: 'POST', body: JSON.stringify(player) }),

  // Matches
  getMatches: (filters = {}) => {
    if (typeof filters === 'string') return request('/matches');
    const params = new URLSearchParams();
    if (filters.team_id) params.set('team_id', filters.team_id);
    if (filters.status) params.set('status', filters.status);
    if (filters.tactical_focus) params.set('tactical_focus', filters.tactical_focus);
    const qs = params.toString();
    return request(`/matches${qs ? `?${qs}` : ''}`);
  },
  createMatch: (match) => request('/matches', { method: 'POST', body: JSON.stringify(match) }),

  // Key moments createKeyMoment passes through all fields including the new
  // evidence fields: phase_of_play, video_timestamp, evidence_notes, tags (array).
  getKeyMoments: (matchId) => request(`/key-moments?match_id=${matchId}`),
  createKeyMoment: (moment) => request('/key-moments', { method: 'POST', body: JSON.stringify(moment) }),

  // Player / role reviews
  getRoleReviews: (matchId) => request(`/role-reviews?match_id=${matchId}`),
  createRoleReview: (review) => request('/role-reviews', { method: 'POST', body: JSON.stringify(review) }),

  // Reports
  getReports: (filters = {}) => {
    // Accept either a plain matchId string (legacy) or a filters object
    if (typeof filters === 'string') return request(`/reports?match_id=${filters}`);
    const params = new URLSearchParams();
    if (filters.match_id) params.set('match_id', filters.match_id);
    if (filters.team_id) params.set('team_id', filters.team_id);
    if (filters.tactical_focus) params.set('tactical_focus', filters.tactical_focus);
    const qs = params.toString();
    return request(`/reports${qs ? `?${qs}` : ''}`);
  },
  getReport: (id) => request(`/reports?id=${id}`),
  getReportForMatch: (matchId) => request(`/reports?match_id=${matchId}`),

  // Team Pattern Library CRUD on stored team patterns
  getPatterns: (teamId) => request(`/patterns${teamId ? `?team_id=${teamId}` : ''}`),
  createPattern: (pattern) => request('/patterns', { method: 'POST', body: JSON.stringify(pattern) }),
  updatePattern: (data) => request('/patterns', { method: 'PATCH', body: JSON.stringify(data) }),
  deletePattern: (id) => request(`/patterns?id=${id}`, { method: 'DELETE' }),

  // Match Memory Search search across reports, key moments, patterns, role reviews
  searchMemory: (q, teamId) => request(`/insights-search?q=${encodeURIComponent(q)}${teamId ? `&team_id=${teamId}` : ''}`),

  // Runs the Tactmark Analysis Engine on a saved match (key moments + role
  // reviews already attached) and returns the generated report.
  analyzeMatch: (matchId, coachDecisions) => request('/analyze', { method: 'POST', body: JSON.stringify(coachDecisions ? { matchId, coachDecisions } : { matchId }) }),
  // Poll generation status for an existing match. Returns { status, reportId }.
  getGenerationStatus: (matchId) => request(`/analyze?matchId=${encodeURIComponent(matchId)}`, { method: 'GET' }),
  // Retry generation on an EXISTING match never recreates the match or moments.
  retryAnalysis: (matchId) => request('/analyze', { method: 'POST', body: JSON.stringify({ matchId }) }),

  // Organization settings
  getOrganization: () => request('/organization'),
  updateOrganization: (data) => request('/organization', { method: 'PATCH', body: JSON.stringify(data) }),

  // Notification preferences
  getNotificationPreferences: () => request('/notification-preferences'),
  updateNotificationPreferences: (preferences) =>
    request('/notification-preferences', { method: 'PATCH', body: JSON.stringify({ preferences }) }),

  // User management
  getUsers: () => request('/users'),
  inviteUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (data) => request('/users', { method: 'PATCH', body: JSON.stringify(data) }),
  assignUserTeams: (data) => request('/users', { method: 'PUT', body: JSON.stringify(data) }),

  // Shareable read-only report links
  createReportShare: (reportId, expiresInDays) =>
    request('/report-share', { method: 'POST', body: JSON.stringify({ reportId, expiresInDays }) }),

  // Report Actions
  getReportActions: (params = {}) => {
    const qs = Object.entries(params).map(([k,v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    return request(`/report-actions${qs ? '?' + qs : ''}`);
  },
  createReportAction: (data) => request('/report-actions', { method: 'POST', body: JSON.stringify(data) }),
  // Update a training_focus_block status by index (goes to /api/reports PATCH)
  updateTrainingFocusBlockStatus: ({ reportId, index, status }) => request('/reports', { method: 'PATCH', body: JSON.stringify({ reportId, index, status }) }),
  // Update a coaching action record (goes to /api/report-actions PATCH)
  updateCoachingAction: (data) => request('/report-actions', { method: 'PATCH', body: JSON.stringify(data) }),
  deleteReportAction: (id) => request(`/report-actions?id=${id}`, { method: 'DELETE' }),

  // Coaching intelligence — findings confirmation + decision memory
  getCoachingFindings: (params = {}) => {
    const qs = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    return request(`/coaching-findings${qs ? `?${qs}` : ''}`);
  },
  updateCoachingFinding: (data) =>
    request('/coaching-findings', { method: 'PATCH', body: JSON.stringify(data) }),
  getCoachingDecisions: (params = {}) => {
    const qs = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    return request(`/coaching-decisions${qs ? `?${qs}` : ''}`);
  },
  createCoachingDecision: (data) =>
    request('/coaching-decisions', { method: 'POST', body: JSON.stringify(data) }),
  updateCoachingDecision: (data) =>
    request('/coaching-decisions', { method: 'PATCH', body: JSON.stringify(data) }),

  // createShareToken alias
  createShareToken: (reportId, expiresInDays = 30) =>
    request('/report-share', { method: 'POST', body: JSON.stringify({ reportId, expiresInDays }) }),

  // Billing (Stripe Checkout)
  createCheckoutSession: (plan) => request('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) }),

  // Download a report PDF from the authenticated backend endpoint.
  // The browser sends only the report_id + auth token; the PDF is built server-side
  // from D1 data (never from client-supplied content). On 503 (Browser Run not
  // configured / errored) we surface the report's graceful message and do NOT
  // fall back to browser print.
  downloadReportPdf: async (reportId) => {
    const token = getToken();
    const res = await fetch(`${BASE}/reports/pdf?report_id=${encodeURIComponent(reportId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let message = 'PDF export is temporarily unavailable. The report remains available online.';
      try {
        const err = await res.json();
        if (err && err.error) message = err.error;
      } catch (_e) { /* keep default */ }
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = (match && match[1]) || 'tactmark-report.pdf';
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  }
};

// Platform Admin data calls always sent with the separate admin token,
// never the client org token. Backend rejects these without a valid
// admin_sessions token regardless of what the caller sends.
export const adminApi = {
  getOverview: () => adminRequest('/platform-admin?section=overview'),
  getOrganizations: (page = 1, limit = 20, q = '') =>
    adminRequest(`/platform-admin?section=organizations&page=${page}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  getOrgs: (page = 1, limit = 20, q = '') =>
    adminRequest(`/platform-admin?section=organizations&page=${page}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  getCountries: () => adminRequest('/platform-admin?section=countries'),
  getFunnel: () => adminRequest('/platform-admin?section=funnel'),
  getReportQuality: () => adminRequest('/platform-admin?section=report_quality'),
  getRecentSignups: () => adminRequest('/platform-admin?section=recent_signups'),
  updateOrg: (data) => adminRequest('/platform-admin', { method: 'PATCH', body: JSON.stringify(data) }),
  getOrgProducts: (organizationId) =>
    adminRequest(`/platform-admin/org-products?organizationId=${encodeURIComponent(organizationId)}`),
  updateOrgProducts: (data) =>
    adminRequest('/platform-admin/org-products', { method: 'PATCH', body: JSON.stringify(data) }),

  getAllInsights: () => adminRequest('/insights?all=true'),
  createShareToken: (reportId) => request('/report-share', { method: 'POST', body: JSON.stringify({ report_id: reportId }) }),
  createInsight: (data) => adminRequest('/insights', { method: 'POST', body: JSON.stringify(data) }),
  updateInsightStatus: (id, status) => adminRequest('/insights', { method: 'PATCH', body: JSON.stringify({ id, status }) })
};

export const publicApi = {
  submitContact: (data) => request('/contact', { method: 'POST', body: JSON.stringify(data) }),
  getSharedReport: (token) => request(`/report-share?token=${encodeURIComponent(token)}`),
  getInsights: (category) => request(`/insights${category ? `?category=${encodeURIComponent(category)}` : ''}`),
  getInsight: (slug) => request(`/insights?slug=${encodeURIComponent(slug)}`)
};

function federationQs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// Federation product API — all endpoints require Federation entitlement server-side.
export const federation = {
  // Programs
  getPrograms: (params = {}) => request(`/programs${federationQs(params)}`),
  createProgram: (data) => request('/programs', { method: 'POST', body: JSON.stringify(data) }),
  updateProgram: (data) => request('/programs', { method: 'PATCH', body: JSON.stringify(data) }),

  // Standalone program submissions
  getSubmissions: (params = {}) => request(`/federation-submissions${federationQs(params)}`),
  createSubmission: (data) =>
    request('/federation-submissions', { method: 'POST', body: JSON.stringify(data) }),
  updateSubmission: (data) =>
    request('/federation-submissions', { method: 'PATCH', body: JSON.stringify(data) }),

  // Program ↔ team membership
  getProgramTeams: (programId) => request(`/program-teams${federationQs({ programId })}`),
  addProgramTeam: (data) => request('/program-teams', { method: 'POST', body: JSON.stringify(data) }),
  removeProgramTeam: (id) => request(`/program-teams?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Development objectives
  getObjectives: (params = {}) => request(`/development-objectives${federationQs(params)}`),
  createObjective: (data) =>
    request('/development-objectives', { method: 'POST', body: JSON.stringify(data) }),
  updateObjective: (data) =>
    request('/development-objectives', { method: 'PATCH', body: JSON.stringify(data) }),

  // Findings
  getFindings: (params = {}) => request(`/federation-findings${federationQs(params)}`),
  createFinding: (data) =>
    request('/federation-findings', { method: 'POST', body: JSON.stringify(data) }),
  updateFinding: (data) =>
    request('/federation-findings', { method: 'PATCH', body: JSON.stringify(data) }),

  // Coaching actions (federation-scoped)
  getActions: (params = {}) => request(`/federation-actions${federationQs(params)}`),
  createAction: (data) =>
    request('/federation-actions', { method: 'POST', body: JSON.stringify(data) }),
  updateAction: (data) =>
    request('/federation-actions', { method: 'PATCH', body: JSON.stringify(data) }),

  // Reviews
  getReviews: (params = {}) => request(`/federation-reviews${federationQs(params)}`),
  createReview: (data) =>
    request('/federation-reviews', { method: 'POST', body: JSON.stringify(data) }),
  updateReview: (data) =>
    request('/federation-reviews', { method: 'PATCH', body: JSON.stringify(data) }),

  // Reporting periods
  getPeriods: (params = {}) => request(`/federation-reporting-periods${federationQs(params)}`),
  createPeriod: (data) =>
    request('/federation-reporting-periods', { method: 'POST', body: JSON.stringify(data) }),
  updatePeriod: (data) =>
    request('/federation-reporting-periods', { method: 'PATCH', body: JSON.stringify(data) }),
  deletePeriod: (id) =>
    request(`/federation-reporting-periods?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Templates
  getTemplates: (params = {}) => request(`/federation-templates${federationQs(params)}`),
  createTemplate: (data) =>
    request('/federation-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateTemplate: (data) =>
    request('/federation-templates', { method: 'PATCH', body: JSON.stringify(data) }),

  // Dashboard + exports
  getDashboard: (params = {}) => request(`/federation-dashboard${federationQs(params)}`),
  getExport: (params = {}) => request(`/federation-exports${federationQs(params)}`),
  downloadExportCsv: async (params = {}) => {
    const token = localStorage.getItem('tactmark_token');
    const response = await fetch(`/api/federation-exports${federationQs({ ...params, format: 'csv' })}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = 'Could not download CSV export';
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch (_) { /* binary/error text */ }
      throw new Error(message);
    }
    return response.blob();
  },

  // Program staff access (Owner only)
  getProgramAccess: (params = {}) => request(`/program-access${federationQs(params)}`),
  assignProgramAccess: (data) =>
    request('/program-access', { method: 'POST', body: JSON.stringify(data) }),
  revokeProgramAccess: (id) =>
    request(`/program-access?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Audit (Owner only)
  getAudit: (params = {}) => request(`/organization-audit${federationQs(params)}`),
};
